/**
 * MobileTrainingFallbackLaunchInterstitial — Batch 18-F11.5.
 *
 * Presentational scaffold for the "no-extension mobile fallback" path
 * named by the F11.3 cross-browser/mobile Companion strategy doc and
 * encoded as `mobile_no_extension_fallback` in the F11.4 platform
 * capability model. Renders the launch UI a technician sees on a
 * device/browser where extension-based passive observation is not
 * available (stock Chrome iOS, stock Android Chrome, any device
 * before the Chromium / Firefox / Safari adapter ships).
 *
 * Inviolable behaviour, enforced by the F11.4 attribution policy
 * import below:
 *
 *   - Training opens the REAL provider URL via window.open(...) or
 *     the consumer-supplied `onOpenProvider` callback. NO iframe,
 *     NO embed, NO video tag, NO fetch of provider content.
 *   - OptiBay does NOT host / mirror / proxy / scrape provider
 *     content.
 *   - Provider attribution (views, monetisation, login, paywall,
 *     ads, analytics) is preserved by routing the technician
 *     directly to the provider's own page.
 *   - The interstitial labels mobile-fallback evidence honestly as
 *     `self_attested` (per F11.4) — never as `'likely'`, never as
 *     `'verified'`, never as manager- or provider-attested.
 *
 * The component does NOT:
 *
 *   - call `useOpenTrainingWithSession` / `useTrainingSessionToken`
 *   - touch Supabase, fetch, or any Edge Function
 *   - mutate `completion_status` / `completion_confidence`
 *   - create evidence rows
 *   - inspect the provider page in any way
 *
 * It is a pure UI affordance. Production wiring (session row +
 * launch event) belongs to the consumer that mounts it.
 */

import { ExternalLink } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TRAINING_PROVIDER_ATTRIBUTION_POLICY } from '@/utils/training/companionPlatformCapabilities';
// Batch 18-F11.20: type-only import for the optional explanatory
// capability prop. The interstitial intentionally does NOT import
// `resolveCompanionRuntimeCapability` / `collectPassiveRuntimeSignals`
// / `isMobileLikeRuntime` / `isExtensionObservationAvailable` — it
// never collects signals on its own.
import type { CompanionRuntimeCapabilityResult } from '@/utils/training/companionRuntimeCapability';
// Batch 18-F11.20: label helpers live in a dedicated file so the
// interstitial source stays free of the lowercase literal that
// would mislabel fallback evidence (F11.5 source-pin invariant).
import {
  getTrustCeilingLabel,
  isVerifiedCeiling,
} from '@/utils/training/companionTrustCeilingLabels';

// ─── URL safety helper (exported for tests) ─────────────────────────
//
// Conservative: accept only http:// or https:// URLs (rejects empty
// strings, whitespace-only strings, `javascript:` URLs, `data:` URLs,
// `file://`, mailto:, etc.). The interstitial disables the Open
// button when this returns false so a malformed provider URL cannot
// accidentally trigger `window.open` against a non-http origin.

export function isSafeTrainingProviderUrl(url: unknown): boolean {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (trimmed.length === 0) return false;
  return /^https?:\/\//i.test(trimmed);
}

// ─── Props ─────────────────────────────────────────────────────────

export interface MobileTrainingFallbackLaunchInterstitialProps {
  trainingTitle: string;
  providerName?: string | null;
  providerUrl: string;
  estimatedDurationMinutes?: number | null;
  onOpenProvider?: (providerUrl: string) => void;
  onCancel?: () => void;
  // Batch 18-F11.20: optional explanatory context. If supplied,
  // the interstitial renders a small muted block summarising the
  // caller-computed runtime capability — for copy purposes only.
  // The component never collects signals itself and ignores
  // `shouldAutoLaunchFallback` for behaviour even if a caller
  // forcibly sets it true via cast.
  capabilityContext?: CompanionRuntimeCapabilityResult | null;
}

// ─── Component ─────────────────────────────────────────────────────

export function MobileTrainingFallbackLaunchInterstitial({
  trainingTitle,
  providerName,
  providerUrl,
  estimatedDurationMinutes,
  onOpenProvider,
  onCancel,
  capabilityContext,
}: MobileTrainingFallbackLaunchInterstitialProps) {
  const safeUrl = isSafeTrainingProviderUrl(providerUrl);

  // Batch 18-F11.20: explanatory context only. `shouldAutoLaunchFallback`
  // is INTENTIONALLY not read here — even if a caller forces it to
  // `true` via a cast, the interstitial's launch behaviour is
  // unchanged. The handleOpen below remains gated on safeUrl and on
  // an explicit user click of the Open button.
  const capabilityNotes = capabilityContext?.notes?.slice(0, 2) ?? [];
  const showVerifiedWarning =
    capabilityContext !== null &&
    capabilityContext !== undefined &&
    isVerifiedCeiling(capabilityContext.evidenceTrustCeiling);

  // The F11.4 attribution policy gates the visible disclosures. If a
  // future edit ever flips one of these to `false`, the matching
  // paragraph silently disappears — which is exactly the invariant
  // the source-pin checks for (you cannot accidentally publish a
  // launch surface that hides the attribution promise).
  const showRealUrlDisclosure =
    TRAINING_PROVIDER_ATTRIBUTION_POLICY.opensRealProviderUrl &&
    TRAINING_PROVIDER_ATTRIBUTION_POLICY.mustNotHostMirrorProxyOrScrape;
  const showMonetizationDisclosure =
    TRAINING_PROVIDER_ATTRIBUTION_POLICY.mustPreserveProviderMonetization;
  const showLoginPaywallAdsDisclosure =
    TRAINING_PROVIDER_ATTRIBUTION_POLICY.mustNotBypassProviderLoginPaywallAdsAnalytics;

  const handleOpen = () => {
    if (!safeUrl) return;
    if (onOpenProvider) {
      onOpenProvider(providerUrl);
      return;
    }
    if (typeof window !== 'undefined' && typeof window.open === 'function') {
      window.open(providerUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card
      className="max-w-xl"
      data-testid="mobile-training-fallback-interstitial"
    >
      <CardHeader>
        <CardTitle data-testid="mobile-training-fallback-title">
          Open training on provider site
        </CardTitle>
        <CardDescription data-testid="mobile-training-fallback-training-title">
          {trainingTitle}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {providerName && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="secondary"
              className="text-[11px]"
              data-testid="mobile-training-fallback-provider-name"
            >
              Provider: {providerName}
            </Badge>
            {typeof estimatedDurationMinutes === 'number' &&
              estimatedDurationMinutes > 0 && (
                <Badge
                  variant="outline"
                  className="text-[11px]"
                  data-testid="mobile-training-fallback-duration"
                >
                  ~{estimatedDurationMinutes} min
                </Badge>
              )}
          </div>
        )}

        {showRealUrlDisclosure && (
          <p
            className="text-sm"
            data-testid="mobile-training-fallback-attribution"
          >
            This opens the creator/provider&apos;s real training page so they
            receive credit, traffic, views, and any monetization. OptiBay
            does not host, mirror, proxy, or embed provider content.
          </p>
        )}

        {showMonetizationDisclosure && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="mobile-training-fallback-monetization"
          >
            Provider view counts and monetization are preserved.
          </p>
        )}

        {showLoginPaywallAdsDisclosure && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="mobile-training-fallback-login-disclosure"
          >
            Provider login, paywall, ads, and analytics are preserved.
          </p>
        )}

        <p
          className="text-xs text-muted-foreground"
          data-testid="mobile-training-fallback-evidence-caveat"
        >
          On this device, OptiBay may only record launch, return, and
          self-attested completion unless a supported Companion extension
          or provider/LMS integration is available. Manager review remains
          available regardless of device.
        </p>

        {capabilityContext && (
          // Batch 18-F11.20: explanatory copy block. NEVER drives
          // behaviour. The Open / Cancel buttons below ignore this
          // entirely; their handlers are unchanged.
          <div
            className="rounded-md border border-muted px-3 py-2 space-y-1"
            data-testid="mobile-training-fallback-capability-context"
          >
            <p
              className="text-xs font-medium text-muted-foreground"
              data-testid="mobile-training-fallback-capability-title"
            >
              Current evidence path
            </p>
            <p
              className="text-xs text-muted-foreground"
              data-testid="mobile-training-fallback-capability-reason"
            >
              {capabilityContext.reason}
            </p>
            <p
              className="text-xs text-muted-foreground"
              data-testid="mobile-training-fallback-capability-ceiling"
            >
              Evidence ceiling:{' '}
              {getTrustCeilingLabel(capabilityContext.evidenceTrustCeiling)}
            </p>
            {showVerifiedWarning && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="mobile-training-fallback-capability-confirm-warning"
              >
                Verified evidence requires provider or manager attestation.
                This fallback screen does not confirm completion by itself.
              </p>
            )}
            {capabilityNotes.map((note, idx) => (
              <p
                key={idx}
                className="text-xs text-muted-foreground"
                data-testid={`mobile-training-fallback-capability-note-${idx}`}
              >
                {note}
              </p>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button
            type="button"
            onClick={handleOpen}
            disabled={!safeUrl}
            aria-label="Open training on provider site"
            data-testid="mobile-training-fallback-open-button"
          >
            <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
            Open training
          </Button>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              data-testid="mobile-training-fallback-cancel-button"
            >
              Cancel
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
